// Client for the ABC × XYZ classification pipeline (/api/classification).
import { USERS } from "./users";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/classification`;

// The signed-in user (gate session) — sent on every call so the API can scope branch managers to their branch.
function signedIn() {
  try {
    const id = JSON.parse(localStorage.getItem("ect-gate-user-v2") || "null")?.employeeId;
    return USERS.find(u => u.employeeId === id) || null;
  } catch { return null; }
}

export function headers(actor, json) {
  const a = actor || signedIn();
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(a ? { "X-Actor": a.employeeId || "", "X-Actor-Role": a.role || "", ...(a.branchId ? { "X-Actor-Branch": a.branchId } : {}) } : {}),
  };
}

export async function parse(res) {
  const text = await res.text();
  let payload = null;
  try { payload = text ? JSON.parse(text) : null; } catch { payload = null; }
  if (!res.ok) {
    const d = payload?.detail;
    const err = new Error(Array.isArray(d) ? d.map(x => x.msg).join("; ") : d || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return payload;
}

const get = (path) => fetch(`${BASE}${path}`, { headers: headers(null, false) }).then(parse);
const send = (method, path, body, actor) =>
  fetch(`${BASE}${path}`, { method, headers: headers(actor, true), body: JSON.stringify(body) }).then(parse);
const upload = (path, file, fields, actor) => {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(fields || {}).forEach(([k, v]) => fd.append(k, v));
  return fetch(`${BASE}${path}`, { method: "POST", headers: headers(actor, false), body: fd }).then(parse);
};
const qs = (o) => new URLSearchParams(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== "")).toString();
const skuPath = (id) => id.split("/").map(encodeURIComponent).join("/");

export async function downloadFile(path, filename, actor) {
  const res = await fetch(`${BASE}${path}`, { headers: headers(actor, false) });
  if (!res.ok) await parse(res);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export const EDITOR_ROLES = ["it_head", "coo"];
export const canEditRules = (user) => EDITOR_ROLES.includes(user?.role);

const bq = (branch) => (branch ? `?branch=${branch}` : "");
export const getSummary = (branch) => get(`/summary${bq(branch)}`);
export const getRules = () => get("/rules");
export const getCell = (code, p) => get(`/cells/${code}?${qs(p)}`);
export const exportCell = (code, p) => downloadFile(`/cells/${code}/export?${qs(p)}`, `abcxyz-${code}.csv`);
export const getSku = (id, branch) => get(`/skus/${skuPath(id)}${bq(branch)}`);
export const getExplanation = (kind, id, branch) => get(`/explain/${kind}/${skuPath(id)}${bq(branch)}`);

// Streams a Copilot class explanation (SSE). A saved explanation arrives as one token followed by `done`.
export async function streamExplanation(kind, id, actor, { onMeta, onToken, onDone, signal, branch }) {
  const res = await fetch(`${BASE}/explain/${kind}/${skuPath(id)}${bq(branch)}`, { method: "POST", headers: headers(actor, false), signal });
  if (!res.ok || !res.body) await parse(res);
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let finished = false;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i;
    while ((i = buf.indexOf("\n\n")) !== -1) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const ev = /^event: (.+)$/m.exec(chunk)?.[1];
      const raw = /^data: (.*)$/m.exec(chunk)?.[1];
      if (!ev || raw === undefined) continue;
      const d = JSON.parse(raw);
      if (ev === "meta") onMeta?.(d);
      else if (ev === "token") onToken?.(d.text);
      else if (ev === "done") { finished = true; onDone?.(d); }
      else if (ev === "error") throw new Error(d.detail);
    }
  }
  if (!finished) throw new Error("The explanation was interrupted. Please try again.");
}
export const getFeatures = () => get("/features");
export const whatIf = (body, actor) => send("POST", "/what-if", body, actor);
export const getChanges = (p, id) => get(`/changes/${id || "latest"}?${qs(p || {})}`);
export const getChangeHistory = (limit = 7) => get(`/changes?limit=${limit}`);
export const getActualStatus = () => get("/actual/status");
export const checkActual = (actor) => fetch(`${BASE}/actual/check`, { method: "POST", headers: headers(actor, false) }).then(parse);
export const previewActual = (actor) => fetch(`${BASE}/actual/preview`, { method: "POST", headers: headers(actor, false) }).then(parse);
export const switchSource = (to, reason, actor) => send("POST", "/actual/switch", { to, reason }, actor);
export const getSettings = () => get("/settings");
export const putSetting = (key, value, reason, actor) => send("PUT", "/settings", { key, value, reason }, actor);
export const getHistory = (limit = 100) => get(`/history?limit=${limit}`);
export const getRuns = (limit = 5) => get(`/runs?limit=${limit}`);
export const runPipeline = (actor) => fetch(`${BASE}/run`, { method: "POST", headers: headers(actor, false) }).then(parse);

export const getLinks = (p) => get(`/links?${qs(p)}`);
export const addLink = (body, actor) => send("POST", "/links", body, actor);
export const editLink = (id, body, actor) => send("PUT", `/links/${id}`, body, actor);
export const retireLink = (id, reason, actor) => send("POST", `/links/${id}/retire`, { reason }, actor);
export const importLinks = (file, reason, actor) => upload("/links/import", file, { reason }, actor);

export const getInstallSummary = () => get("/install-base/summary");
export const validateInstallBase = (file, actor) => upload("/install-base/validate", file, {}, actor);
export const uploadInstallBase = (file, reason, actor) => upload("/install-base/upload", file, { reason }, actor);
export const downloadUpload = (u, actor) => downloadFile(`/uploads/${u.id}/download`, u.original_filename, actor);
